import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { FirebaseError } from 'firebase/app';
import { TranslateService } from '@ngx-translate/core';
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  Messaging,
  MessagePayload,
} from 'firebase/messaging';
import { getInstallations, getId } from 'firebase/installations';
import { firebaseApp } from '../../firebase/firebase.config';
import { ApiNotificationsService } from '../api/api-notifications.service';
import { PaginationOptions, Notification, normalizeNotification } from '@meplis/common';
import { PlatformService } from '@meplis/services/platform';
import { environment } from '@meplis/environments';

/** How long to wait on APNs/FCM before giving up and listing anyway. */
const PUSH_REGISTRATION_TIMEOUT_MS = 8000;
/** Android only, and the same id AndroidManifest.xml declares to FCM. */
const ANDROID_NOTIFICATION_CHANNEL_ID = 'meplis_default';
// Not static imports: this service is shared with apps/meplis-cnp (web),
// whose bundler has to resolve every top-level import even inside an
// `if (platform === 'mobile')` branch -- @capacitor/* packages aren't part
// of the web app's dependency graph at all, so a static import here breaks
// web's dev server ("Failed to resolve module specifier '@capacitor/core'").
// Loaded lazily instead, only when actually reached on mobile. Type-only
// imports are erased at compile time and cost nothing, so those stay static.
import type {
  PushNotificationSchema,
  Token as CapacitorPushToken,
} from '@capacitor/push-notifications';

export interface NotificationPayload {
  messageId?: string;
  from?: string;
  collapseKey?: string;
  data?: Record<string, string>;
  notification?: {
    title?: string;
    body?: string;
    [key: string]: string | undefined;
  };
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private _apiNotificationsService = inject(ApiNotificationsService);
  private _platformId = inject(PLATFORM_ID);
  private _translateService = inject(TranslateService);
  private _platformService = inject(PlatformService);

  private messaging: Messaging | null = null;
  private _messagingSupportPromise: Promise<boolean> | null = null;
  private _foregroundListenerInitialized = false;
  private readonly _messages$ = new BehaviorSubject<NotificationPayload | null>(
    null
  );
  private readonly _pushToken$ = new BehaviorSubject<string | null>(null);
  // Tracks the last identifier successfully registered with the backend so
  // that pagination ("load more") and repeat visits (opening the menu again,
  // "Show all") don't each re-POST /push-notifications for a token the
  // backend already has on file -- only a genuinely new/rotated token
  // triggers another registration call.
  private _registeredIdentifier: string | null = null;
  private _registrationInFlight$: Observable<void> | null = null;
  /** Resolves once with the APNs/FCM token from the native `registration`
   * event -- cached so a second caller (e.g. re-opening the chat list)
   * doesn't call `PushNotifications.register()` again. */
  private _mobilePushTokenPromise: Promise<string | null> | null = null;
  private readonly _notificationTapped$ =
    new BehaviorSubject<NotificationPayload | null>(null);

  public readonly messages$: Observable<NotificationPayload | null> =
    this._messages$.asObservable();
  public readonly pushToken$: Observable<string | null> =
    this._pushToken$.asObservable();
  /**
   * Mobile only: fires when the user taps a push notification (app was in
   * background or closed) rather than one arriving while the app is already
   * open (that's `messages$`). Web has no equivalent -- Firebase's
   * `onMessage` only ever covers the foreground case; a background/closed
   * tap on web is handled by `firebase-messaging-sw.js` itself, outside
   * Angular entirely.
   */
  public readonly notificationTapped$: Observable<NotificationPayload | null> =
    this._notificationTapped$.asObservable();

  private get _isPlatformBrowser(): boolean {
    return isPlatformBrowser(this._platformId);
  }

  public get pushToken(): string | null {
    return this._pushToken$.value;
  }

  public init(): void {
    if (this._platformService.platform === 'mobile') {
      void this._initializeMobileForegroundListener();
      void this._ensureAndroidChannel();
      return;
    }
    void this.initializeForegroundListener();
  }

  /**
   * Android 8 and up files every notification under a channel, and the user
   * controls sound, vibration and importance per channel. Declaring the id in
   * the manifest is only half of it: a channel the app never creates shows up
   * in the system settings unnamed, so this creates it with a real name the
   * first time the app runs. Creating it again is a no-op, and a channel that
   * already exists is never overwritten -- Android keeps whatever the user
   * has since chosen for it.
   */
  private async _ensureAndroidChannel(): Promise<void> {
    const { Capacitor } = await import('@capacitor/core');

    if (Capacitor.getPlatform() !== 'android') {
      return;
    }

    const { PushNotifications } = await import('@capacitor/push-notifications');

    try {
      await PushNotifications.createChannel({
        // Matches default_notification_channel_id in AndroidManifest.xml.
        id: ANDROID_NOTIFICATION_CHANNEL_ID,
        name: this._translateService.instant('NOTIFICATIONS.TITLE'),
        importance: 4,
        visibility: 1,
      });
    } catch (error) {
      console.warn(
        '[NotificationService] Unable to create the Android notification channel.',
        error
      );
    }
  }

  public async initializePushToken(
    vapidKey?: string,
    serviceWorkerScript = '/firebase-messaging-sw.js'
  ): Promise<string | null> {
    if (this._platformService.platform === 'mobile') {
      return this._initializeMobilePushToken();
    }

    const messaging = await this.getMessagingIfSupported();
    if (!messaging) {
      return null;
    }

    const registration = await this.registerServiceWorker(serviceWorkerScript);
    const token = await this.requestToken(
      vapidKey,
      registration ?? undefined,
      messaging
    );
    if (token) {
      this._pushToken$.next(token);
    }
    return token;
  }

  public async ensurePushToken(vapidKey?: string): Promise<string | null> {
    if (this.pushToken) {
      return this.pushToken;
    }

    return this.initializePushToken(vapidKey);
  }

  public getNotifications(
    paginationOptions: PaginationOptions,
    type?: 'WEB' | 'ANDROID' | 'IOS'
  ): Observable<Notification[]> {
    return from(
      Promise.all([
        this.ensurePushToken(),
        this.getInstallationId(),
        type ? Promise.resolve(type) : this._resolveDeviceType(),
      ])
    ).pipe(
      switchMap(([token, deviceId, resolvedType]) => {
        // Registering the push token is a side effect; listing is the point.
        // On mobile the two must not be coupled: the iOS Simulator never
        // issues an APNs token at all, and on a real device the user can
        // decline notifications -- either way the screen would be
        // permanently empty for a list the backend is perfectly willing to
        // serve. Web keeps its original behaviour untouched.
        const isMobile = this._platformService.platform === 'mobile';

        if (!token) {
          return isMobile
            ? this._apiNotificationsService
                .getNotifications(paginationOptions)
                .pipe(map((notifications) => this._normalize(notifications)))
            : of([]);
        }

        return this._ensureTokenRegistered(token, resolvedType, deviceId).pipe(
          // A failed registration must not take the list down with it.
          catchError((error) => {
            console.warn(
              '[NotificationService] Token registration failed; listing anyway.',
              error
            );
            return of(undefined);
          }),
          switchMap(() =>
            this._apiNotificationsService.getNotifications(paginationOptions)
          ),
          map((notifications) => this._normalize(notifications))
        );
      })
    );
  }

  /**
   * Same normalizer the realtime channel uses, so a notification reads
   * identically regardless of how it arrived. REST already carries its own
   * title/description; the practical fix here is `at` -> `createdAt`, since
   * the wire field never matched the model's name for it.
   */
  private _normalize(notifications: Notification[]): Notification[] {
    return notifications.map((raw) =>
      normalizeNotification(raw, (key) => this._translateService.instant(key))
    );
  }

  private _ensureTokenRegistered(
    token: string,
    type: 'WEB' | 'ANDROID' | 'IOS',
    deviceId: string | null
  ): Observable<void> {
    if (this._registeredIdentifier === token) {
      return of(undefined);
    }

    if (!this._registrationInFlight$) {
      this._registrationInFlight$ = this._apiNotificationsService
        .registerNotificationToken(token, type, deviceId)
        .pipe(
          tap(() => {
            this._registeredIdentifier = token;
          }),
          finalize(() => {
            this._registrationInFlight$ = null;
          }),
          shareReplay(1)
        );
    }

    return this._registrationInFlight$;
  }

  /**
   * Stable per-installation ID from Firebase Installations -- unlike the FCM
   * token (which Firebase rotates periodically without our control and
   * without any signal that the old one is gone), this stays the same for
   * the life of the browser install and only changes on uninstall/clear
   * storage. Sent alongside the token so the backend can key registrations
   * by device and retire the previous token for that device instead of
   * accumulating one row per rotation.
   */
  public async getInstallationId(): Promise<string | null> {
    // Firebase Installations is a web-only concept here -- on mobile the
    // push token itself already identifies the device/install, and the
    // registration endpoint's `deviceId` is optional.
    if (
      this._platformService.platform === 'mobile' ||
      !this._isPlatformBrowser
    ) {
      return null;
    }

    try {
      return await getId(getInstallations(firebaseApp));
    } catch (error) {
      console.warn(
        '[NotificationService] Unable to resolve Firebase Installation ID.',
        error
      );
      return null;
    }
  }

  public async registerServiceWorker(
    scriptUrl = '/firebase-messaging-sw.js'
  ): Promise<ServiceWorkerRegistration | null> {
    if (!this._isPlatformBrowser) {
      console.warn(
        '[NotificationService] Service workers are unavailable because the platform is not a browser.'
      );
      return null;
    }

    const supportsMessaging = await this.isMessagingSupported();
    if (!supportsMessaging) {
      console.warn(
        '[NotificationService] Firebase Messaging is not supported in this browser.'
      );
      return null;
    }

    if (!('serviceWorker' in navigator)) {
      console.warn(
        '[NotificationService] Service workers are unavailable in this browser.'
      );
      return null;
    }

    try {
      if (!this._isPlatformBrowser) {
        return null;
      }
      await navigator.serviceWorker.register(scriptUrl);
      return await navigator.serviceWorker.ready;
    } catch (error) {
      console.warn(
        '[NotificationService] Service worker registration failed.',
        error
      );
      return null;
    }
  }

  public async requestToken(
    vapidKey?: string,
    serviceWorkerRegistration?: ServiceWorkerRegistration,
    messaging?: Messaging
  ): Promise<string | null> {
    let registration = serviceWorkerRegistration;
    const resolvedMessaging =
      messaging ?? (await this.getMessagingIfSupported());

    if (!this._isPlatformBrowser) {
      console.warn(
        '[NotificationService] Cannot request FCM token because the platform is not a browser.'
      );
      return null;
    }

    if (!resolvedMessaging) {
      return null;
    }

    if (!('serviceWorker' in navigator)) {
      console.warn(
        '[NotificationService] Cannot request FCM token because service workers are unavailable in this browser.'
      );
      return null;
    }

    if (!registration) {
      console.warn(
        '[NotificationService] No service worker registration provided. Attempting to wait for an active registration.'
      );
      registration = await navigator.serviceWorker.ready;
    }

    if (!registration) {
      console.warn(
        '[NotificationService] Service worker registration is unavailable. Cannot request FCM token.'
      );
      return null;
    }

    try {
      const token = await getToken(resolvedMessaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });
      return token || null;
    } catch (error) {
      if (
        error instanceof FirebaseError &&
        error.code === 'messaging/failed-service-worker-registration'
      ) {
        console.warn(
          '[NotificationService] FCM token unavailable because the service worker registration failed.',
          error
        );
      } else {
        console.warn('[NotificationService] FCM token unavailable.', error);
      }
      return null;
    }
  }

  public async isMessagingSupported(): Promise<boolean> {
    if (!this._isPlatformBrowser) {
      return false;
    }

    if (!this._messagingSupportPromise) {
      this._messagingSupportPromise = isSupported().catch((error) => {
        console.warn(
          '[NotificationService] Unable to verify Firebase Messaging support.',
          error
        );
        return false;
      });
    }

    return this._messagingSupportPromise;
  }

  private async getMessagingIfSupported(): Promise<Messaging | null> {
    if (this.messaging) {
      return this.messaging;
    }

    const supportsMessaging = await this.isMessagingSupported();
    if (!supportsMessaging) {
      return null;
    }

    this.messaging = getMessaging(firebaseApp);
    return this.messaging;
  }

  private async initializeForegroundListener(): Promise<void> {
    if (this._foregroundListenerInitialized) {
      return;
    }

    const messaging = await this.getMessagingIfSupported();
    if (!messaging) {
      return;
    }

    this._foregroundListenerInitialized = true;
    onMessage(messaging, (payload: MessagePayload) => {
      const notification = this.toNotificationPayload(payload);
      this._messages$.next(notification);
    });
  }

  private toNotificationPayload(payload: MessagePayload): NotificationPayload {
    return {
      messageId: payload.messageId,
      from: payload.from,
      collapseKey: payload.collapseKey,
      data: payload.data ? { ...payload.data } : undefined,
      notification: payload.notification
        ? {
            title: payload.notification.title,
            body: payload.notification.body,
            ...payload.notification,
          }
        : undefined,
    };
  }

  // -------------------------------------------------------------------
  // Mobile (Capacitor) -- native APNs/FCM token, no service worker.
  // -------------------------------------------------------------------

  private async _resolveDeviceType(): Promise<'WEB' | 'ANDROID' | 'IOS'> {
    if (this._platformService.platform !== 'mobile') {
      return 'WEB';
    }
    const { Capacitor } = await import('@capacitor/core');
    return Capacitor.getPlatform() === 'ios' ? 'IOS' : 'ANDROID';
  }

  /**
   * Requests notification permission (prompts on iOS the first time; Android
   * 12 and below is always granted) and registers with APNs/FCM. Resolves
   * with the token from the native `registration` event, or `null` if the
   * user denies permission or registration fails -- mirrors the web path's
   * "no token available" outcome so callers don't need a mobile-specific
   * branch of their own.
   */
  private async _initializeMobilePushToken(): Promise<string | null> {
    if (this._mobilePushTokenPromise) {
      return this._mobilePushTokenPromise;
    }

    const { PushNotifications } = await import('@capacitor/push-notifications');

    this._mobilePushTokenPromise = new Promise<string | null>((resolve) => {
      // APNs answers `register()` asynchronously and is not guaranteed to
      // answer at all: on the iOS Simulator neither `registration` nor
      // `registrationError` ever fires, and on a real device the user can
      // leave the permission prompt sitting there. Without a deadline this
      // promise never settles, and everything awaiting it stalls -- which is
      // exactly what left the notifications list on its skeleton forever,
      // since `getNotifications()` waits on this before fetching anything.
      const deadline = setTimeout(() => {
        console.warn(
          '[NotificationService] Push registration did not answer in time.'
        );
        resolve(null);
      }, PUSH_REGISTRATION_TIMEOUT_MS);

      const settle = (value: string | null) => {
        clearTimeout(deadline);
        resolve(value);
      };

      PushNotifications.addListener(
        'registration',
        (token: CapacitorPushToken) => {
          // Printed outside production so a test push can be sent to this
          // install without waiting on the backend: `adb logcat | grep
          // "\[Push\]"` on Android, then tools/scripts/send-test-push.mjs.
          // On Android this is an FCM token; on iOS it is the raw APNs one,
          // which FCM cannot address -- see the comment on _resolveDeviceType.
          if (!environment.production) {
            console.log('[Push] registration token:', token.value);
          }

          this._pushToken$.next(token.value);
          settle(token.value);
        }
      );
      PushNotifications.addListener('registrationError', (error) => {
        console.warn(
          '[NotificationService] Mobile push registration failed.',
          error
        );
        settle(null);
      });

      PushNotifications.checkPermissions()
        .then((status) =>
          status.receive === 'granted'
            ? status
            : PushNotifications.requestPermissions()
        )
        .then((status) => {
          if (status.receive !== 'granted') {
            settle(null);
            return;
          }
          return PushNotifications.register();
        })
        .catch((error) => {
          console.warn(
            '[NotificationService] Unable to request push permissions.',
            error
          );
          settle(null);
        });
    });

    return this._mobilePushTokenPromise;
  }

  /**
   * Feeds the same `messages$` stream the web (Firebase `onMessage`) path
   * does, so a consumer like a future notification badge doesn't need to
   * know which platform it's running on.
   */
  private async _initializeMobileForegroundListener(): Promise<void> {
    if (this._foregroundListenerInitialized) {
      return;
    }
    this._foregroundListenerInitialized = true;

    const { PushNotifications } = await import('@capacitor/push-notifications');

    await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        this._messages$.next(this._toMobileNotificationPayload(notification));
      }
    );

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action) => {
        this._notificationTapped$.next(
          this._toMobileNotificationPayload(action.notification)
        );
      }
    );
  }

  private _toMobileNotificationPayload(
    notification: PushNotificationSchema
  ): NotificationPayload {
    return {
      messageId: notification.id,
      data: notification.data ? { ...notification.data } : undefined,
      notification: {
        title: notification.title,
        body: notification.body,
      },
    };
  }
}
